import {
  signInWithPopup,
  signInWithCredential,
  GoogleAuthProvider,
  signOut as firebaseSignOut,
  onAuthStateChanged,
} from 'firebase/auth';
import { doc, setDoc, serverTimestamp } from 'firebase/firestore';
import { auth, db, googleProvider } from './firebase';
import { ALLOWED_EMAILS } from '@/constants';

/** Returns true when running inside a Capacitor native wrapper */
function isCapacitor() {
  return typeof window !== 'undefined' &&
    typeof window.Capacitor !== 'undefined' &&
    window.Capacitor?.isNativePlatform?.() === true;
}

/** Upsert allowed user profile in Firestore, returns the user */
async function processSignedInUser(user) {
  if (!ALLOWED_EMAILS.includes(user.email)) {
    await firebaseSignOut(auth);
    const err = new Error('Access denied. This app is for private use only.');
    err.code = 'ACCESS_DENIED';
    throw err;
  }

  await setDoc(
    doc(db, 'users', user.uid),
    {
      uid:         user.uid,
      email:       user.email,
      displayName: user.displayName,
      photoURL:    user.photoURL,
      lastLogin:   serverTimestamp(),
    },
    { merge: true }
  );

  return user;
}

/**
 * Sign in with Google.
 * - On native Capacitor: uses the native Google Sign-In plugin, then
 *   exchanges the ID token for a Firebase credential.
 * - On web: uses Firebase popup.
 */
export async function signInWithGoogle() {
  if (isCapacitor()) {
    // Dynamic import so the plugin is only loaded on native
    const { GoogleAuth } = await import('@southdevs/capacitor-google-auth');
    await GoogleAuth.initialize({
      scopes: ['profile', 'email'],
    });
    const googleUser = await GoogleAuth.signIn();
    const idToken = googleUser.authentication.idToken;
    const credential = GoogleAuthProvider.credential(idToken);
    const result = await signInWithCredential(auth, credential);
    return processSignedInUser(result.user);
  }

  // Web: popup works fine in a real browser
  const result = await signInWithPopup(auth, googleProvider);
  return processSignedInUser(result.user);
}

/**
 * No-op kept for API compatibility — redirect flow is no longer used.
 * Returns null (no pending redirect result).
 */
export async function handleRedirectResult() {
  return null;
}

/** Sign out the current user */
export async function signOut() {
  await firebaseSignOut(auth);
}

/**
 * Subscribe to auth state changes.
 * @param {Function} callback  (user | null) => void
 * @returns Unsubscribe function
 */
export function onAuthStateChange(callback) {
  return onAuthStateChanged(auth, (user) => {
    // Extra guard: if somehow a non-allowed user sneaks in, treat as signed out
    if (user && !ALLOWED_EMAILS.includes(user.email)) {
      firebaseSignOut(auth);
      callback(null);
    } else {
      callback(user);
    }
  });
}

/** Check if an email is in the allowlist */
export function isAllowedEmail(email) {
  return ALLOWED_EMAILS.includes(email);
}
